import { useState } from 'react';
import {
  fileIncidentReport,
  incidentSign,
  incidentStateReportFiled,
  incidentToldHowText,
  incidentTypes,
  type ApiClient,
  type IncidentActionResponse,
  type IncidentStamp,
  type ApiError,
  type IncidentInjury,
  type IncidentReport,
  type IncidentReportsResponse,
  type IncidentSignBlocker,
  type IncidentType,
} from '@aithora/shared';
import { ErrorNote } from '../../../ErrorNote';
import { BuildLabel } from '../../../ui/buildLabels';
import { useQuery, type QueryState } from '../../../useQuery';
import { A, sx } from '../parts';
import { PocDialog } from './PocDialog';
import { CARD, HEAD, box, chip, day, hhmm, left, prim } from './styles';
import { TellDoctorPanel, TellFamilyPanel } from './TellPanels';

export interface ResidentOpt {
  id: string;
  label: string;
}

const INJURY: [string, IncidentInjury][] = [['Yes', 'yes'], ['No', 'no'], ['Not sure', 'not_sure']];

/**
 * Report an incident (design pwLayer pwIsInc): type, who, witnessed, injury, the reporter's
 * own words and the type's immediate steps. POST /guardian/incident-reports files it as a
 * draft for the charge nurse.
 */
export function ReportIncidentSheet({ client, residents, chargeName, onClose, onFiled }: {
  client: ApiClient;
  residents: ResidentOpt[];
  /** The charge nurse on shift, when the rota names one. */
  chargeName: string | null;
  onClose: () => void;
  onFiled: (toast: string) => void;
}) {
  const types = useQuery((signal) => incidentTypes(client, { signal }), [client]);
  const [type, setType] = useState<IncidentType | ''>('');
  const [who, setWho] = useState('');
  const [wit, setWit] = useState('');
  const [inj, setInj] = useState('');
  const [serious, setSerious] = useState('');
  const [what, setWhat] = useState('');
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const t = types.data?.types.find((x) => x.type === type) ?? null;
  const steps = t?.immediateSteps ?? [];
  const ready = !!type && !!who && !!what.trim();
  // A serious injury is asked once there may be an injury: Yes moves the state report to 2 hours.
  const askSerious = inj === 'Yes' || inj === 'Not sure';
  const send = async () => {
    if (!ready || busy || !type) return;
    setBusy(true);
    setError(null);
    try {
      const injury = INJURY.find(([l]) => l === inj)?.[1];
      const r = await fileIncidentReport(client, {
        body: {
          type,
          residentId: who,
          whatHappened: what.trim(),
          ...(wit ? { witnessed: wit === 'Yes' } : {}),
          ...(injury ? { injury } : {}),
          ...(askSerious && serious ? { seriousInjury: serious === 'Yes' } : {}),
          immediateSteps: steps.filter((s) => ticked[s]),
        },
      });
      const told = r.told[0];
      const fall = r.report.postFallChecks.length ? ' Post-fall checks added.' : '';
      onFiled(told ? `Sent. ${told.who}: ${told.what}${fall}` : `Saved as a draft. It stays a draft until it is signed.${fall}`);
    } catch (e) {
      setError(e as ApiError);
      setBusy(false);
    }
  };
  const rules = types.data?.rules;

  return (
    <PocDialog
      title="Report an incident"
      onClose={onClose}
      testId="poc-incident"
      action={
        <A style={prim(ready, true)} onClick={() => void send()} disabled={!ready || busy}>
          {chargeName ? `Send to ${chargeName}` : 'Send to the charge nurse'}
        </A>
      }
    >
      {types.error && <ErrorNote error={types.error} onRetry={types.reload} />}
      <div style={sx(HEAD)}>What happened</div>
      <div role="group" aria-label="What happened" style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
        {(types.data?.types ?? []).map((x) => (
          <A
            key={x.type}
            style={chip(type === x.type, x.type === 'Fall')}
            onClick={() => {
              setType(x.type);
              setTicked({});
            }}
          >
            {x.type}
          </A>
        ))}
      </div>
      <div style={sx(HEAD)}>Who</div>
      <div style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
        {residents.map((r) => (
          <A key={r.id} style={chip(who === r.id)} onClick={() => setWho(who === r.id ? '' : r.id)}>
            {r.label}
          </A>
        ))}
      </div>
      <div style={sx('display:flex;gap:18px;flex-wrap:wrap;')}>
        <div style={sx('display:flex;flex-direction:column;gap:6px;')}>
          <div style={sx(HEAD)}>Did someone see it?</div>
          <div role="group" aria-label="Did someone see it?" style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
            {['Yes', 'No'].map((x) => (
              <A key={x} style={chip(wit === x)} onClick={() => setWit(x)}>
                {x}
              </A>
            ))}
          </div>
        </div>
        <div style={sx('display:flex;flex-direction:column;gap:6px;')}>
          <div style={sx(HEAD)}>Any injury?</div>
          <div role="group" aria-label="Any injury?" style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
            {INJURY.map(([x]) => (
              <A
                key={x}
                style={chip(inj === x, x === 'Yes')}
                onClick={() => {
                  setInj(x);
                  if (x === 'No') setSerious('');
                }}
              >
                {x}
              </A>
            ))}
          </div>
        </div>
        {askSerious && (
          <div style={sx('display:flex;flex-direction:column;gap:6px;')}>
            <div style={sx(HEAD)}>Serious injury?</div>
            <div role="group" aria-label="Serious injury?" style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
              {['Yes', 'No'].map((x) => (
                <A key={x} style={chip(serious === x, x === 'Yes')} onClick={() => setSerious(x)}>
                  {x}
                </A>
              ))}
            </div>
          </div>
        )}
      </div>
      {askSerious && serious === 'Yes' && (
        <div role="note" data-testid="incident-serious-note" style={sx('padding:9px 11px;border-radius:9px;background:#FBEDEC;border:1px solid rgba(179,38,30,.4);font-size:16px;font-weight:700;color:#8A1C16;line-height:1.45;')}>
          A serious injury puts the state report on a 2-hour clock (the federal rule; your state may be stricter). It starts when you send, and the charge nurse files the report.
        </div>
      )}
      <div style={sx(HEAD)}>In your words</div>
      <textarea
        value={what}
        onChange={(e) => setWhat(e.target.value)}
        placeholder="Where, what you saw, what you did."
        aria-label="What happened, in your words"
        style={sx('box-sizing:border-box;width:100%;min-height:84px;padding:9px 11px;border-radius:9px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-family:inherit;font-size:16px;line-height:1.45;color:#22272E;resize:vertical;')}
      />
      {steps.length > 0 && (
        <>
          <div style={sx(HEAD)}>What you did right away</div>
          <div style={sx('display:flex;flex-direction:column;background:#FFFFFF;border:1px solid rgba(34,39,46,.12);border-radius:10px;padding:2px 12px;')}>
            {steps.map((s) => {
              const on = !!ticked[s];
              return (
                <button
                  key={s}
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  className="wl-a"
                  onClick={() => setTicked((p) => ({ ...p, [s]: !on }))}
                  style={sx('display:flex;align-items:center;gap:10px;min-height:40px;border-bottom:1px solid rgba(34,39,46,.08);font-size:16px;font-weight:600;color:#22272E;text-decoration:none;')}
                >
                  <span style={sx(box(on))}>{on ? '✓' : ''}</span>
                  <span style={sx('min-width:0;')}>{s}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
      <span
        title={rules?.photo}
        style={sx('display:flex;align-items:center;min-height:40px;padding:0 12px;border-radius:9px;border:1px dashed rgba(34,39,46,.35);background:#FFFFFF;font-size:16px;font-weight:700;color:#54606C;text-decoration:none;')}
      >
        Add a photo · not available yet
      </span>
      {t?.postFallChecks && rules && (
        <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>{`A fall adds post-fall checks to the To do list: every shift for ${rules.postFallHours} hours.`}</span>
      )}
      <div style={sx('display:flex;align-items:flex-start;gap:8px;padding:9px 11px;border-radius:9px;background:#FFFFFF;border:1px solid rgba(34,39,46,.14);')}>
        <span style={sx('flex:0 0 auto;font-size:14px;font-weight:800;letter-spacing:.08em;color:#8A5A00;border:1px solid rgba(138,90,0,.4);border-radius:4px;padding:1px 5px;margin-top:2px;')}>DRAFT</span>
        <span style={sx('font-size:16px;line-height:1.45;color:#3C454F;')}>
          {`This is a draft. ${chargeName ? `${chargeName} (charge)` : 'The charge nurse on shift'} reviews it and tells the family. Nothing is final until it is signed.`}
        </span>
      </div>
      {error && <ErrorNote error={error} />}
    </PocDialog>
  );
}

/** "Family told · 2:14 PM · in the app", "Doctor told · 2:20 PM · by phone, spoke with Dr. Lee". */
export function toldChip(what: 'Doctor told' | 'Family told', st: IncidentStamp): string {
  const how = incidentToldHowText(st.how);
  return `${what} · ${hhmm(st.at)}${how ? ` · ${how}` : ''}`;
}

const BLOCKER_WORDS: Record<IncidentSignBlocker, string> = { doctor_told: 'tell the doctor', family_told: 'tell the family', state_report_filed: 'mark the state report filed' };

/** The 409's missing list in plain words, when the server sent no sentence of its own. */
export function blockersText(list: IncidentSignBlocker[]): string {
  const parts = list.map((b) => BLOCKER_WORDS[b]).filter(Boolean);
  if (!parts.length) return 'This report cannot be signed yet.';
  const s = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `${s[0].toUpperCase()}${s.slice(1)} before you sign.`;
}

type CardState = { busy?: boolean; why?: string; error?: ApiError };

/**
 * The charge nurse inbox (design P.vm inbox): the open drafts, each with its state report
 * clock and investigation date, and Tell the doctor, Tell the family, Mark state report
 * filed and Sign for the charge nurse on shift or the Facility Admin. Others read the
 * reports they filed. Who may act is the server's answer (scope.canAct), never guessed here.
 */
export function InboxSheet({ client, q, now, onClose, onToast }: {
  client: ApiClient;
  q: QueryState<IncidentReportsResponse>;
  now: number;
  onClose: () => void;
  onToast: (text: string) => void;
}) {
  const types = useQuery((signal) => incidentTypes(client, { signal }), [client]);
  const [cards, setCards] = useState<Record<string, CardState>>({});
  // Which Tell step is open on which card (one at a time per card).
  const [telling, setTelling] = useState<Record<string, 'doctor' | 'family' | null>>({});
  const setCard = (id: string, s: CardState) => setCards((p) => ({ ...p, [id]: s }));
  const canAct = q.data?.scope.canAct ?? false;
  const reports = q.data?.reports ?? [];

  const told = (r: IncidentReport, res: IncidentActionResponse, toast: string) => {
    setTelling((p) => ({ ...p, [r.id]: null }));
    q.setData((p) => (p ? { ...p, reports: p.reports.map((x) => (x.id === r.id ? res.report : x)) } : p));
    onToast(toast);
    q.reload();
  };

  const act = async (r: IncidentReport, kind: 'filed' | 'sign') => {
    if (cards[r.id]?.busy) return;
    setCard(r.id, { busy: true });
    const fn = kind === 'filed' ? incidentStateReportFiled : incidentSign;
    try {
      const res = await fn(client, { params: { id: r.id }, body: {} });
      setCard(r.id, {});
      if (kind === 'sign') {
        q.setData((p) => (p ? { ...p, count: Math.max(0, p.count - 1), reports: p.reports.filter((x) => x.id !== r.id) } : p));
        const due = res.investigationDueAt ?? res.report.investigationDueAt;
        onToast(`Signed. It is in the Truth Ledger.${due ? ` Investigation due ${day(due)}.` : ''}`);
      } else {
        q.setData((p) => (p ? { ...p, reports: p.reports.map((x) => (x.id === r.id ? res.report : x)) } : p));
      }
      q.reload();
    } catch (e) {
      const err = e as ApiError;
      const body = err.body as { error?: unknown; signBlockers?: IncidentSignBlocker[] } | null;
      if (kind === 'sign' && err.status === 409) {
        // The server's sentence ("Tell the doctor and tell the family before you sign."), else its list in words.
        setCard(r.id, { why: typeof body?.error === 'string' ? err.message : blockersText(Array.isArray(body?.signBlockers) ? body!.signBlockers : []) });
        q.reload();
      } else setCard(r.id, { error: err });
    }
  };

  const stampLine = (r: IncidentReport) =>
    [
      r.doctorTold ? toldChip('Doctor told', r.doctorTold) : 'Doctor not told yet',
      r.familyTold ? toldChip('Family told', r.familyTold) : 'Family not told yet',
      ...(r.stateReport.required ? [r.stateReport.filed ? `State report filed · ${hhmm(r.stateReport.filed.at)}` : 'State report not filed yet'] : []),
    ].join(' · ');

  const escText = (r: IncidentReport) => {
    const don = Math.round((Date.parse(r.escalation.toDonAt) - Date.parse(r.reportedAt)) / 60_000);
    const adm = Math.round((Date.parse(r.escalation.toAdministratorAt) - Date.parse(r.reportedAt)) / 60_000);
    if (r.escalation.sentToAdministrator) return `Unsigned at ${adm} min: sent to the administrator at ${hhmm(r.escalation.sentToAdministrator.at)}.`;
    if (r.escalation.sentToDon) return `Unsigned at ${don} min: sent to the DON at ${hhmm(r.escalation.sentToDon.at)}. At ${adm} min it goes to the administrator.`;
    return `Unsigned at ${don} min goes to the DON, at ${adm} min to the administrator.`;
  };

  const clock = (r: IncidentReport) => {
    const s = r.stateReport;
    if (!s.required || s.filed) return null;
    if (!s.deadline.deadlineAt) return { text: s.deadline.reason ?? 'A state report is needed. No deadline could be worked out, so check your state rule.', red: false };
    const ms = Date.parse(s.deadline.deadlineAt) - now;
    const over = s.overdue || ms < 0;
    return { text: over ? `State report OVERDUE by ${left(ms)}` : `State report due in ${left(ms)}`, red: over || ms < 30 * 60_000 };
  };

  return (
    <PocDialog title="Charge nurse inbox" onClose={onClose} testId="poc-inbox">
      {q.error && <ErrorNote error={q.error} onRetry={q.reload} />}
      {q.loading && !q.data && <span style={sx('font-size:16px;color:#54606C;')}>Loading the inbox</span>}
      {q.data && !canAct && (
        <div role="note" style={sx('padding:10px 12px;border-radius:10px;background:rgba(47,92,151,.06);font-size:16px;color:#22272E;line-height:1.45;')}>
          These are the reports you filed. The charge nurse on shift or the Facility Admin tells the doctor and the family, and signs them.
        </div>
      )}
      {q.data && reports.length === 0 && (
        <div role="status" style={sx('display:flex;flex-direction:column;gap:7px;padding:12px;border-radius:10px;background:#FFFFFF;border:1px solid rgba(34,39,46,.12);font-size:16px;color:#3C454F;')}>
          {canAct ? 'Nothing waiting. New incident drafts land here.' : 'Nothing waiting. Reports you file show here until they are signed.'}
        </div>
      )}
      {reports.map((r) => {
        const c = cards[r.id] ?? {};
        const ck = clock(r);
        const ready = r.signBlockers.length === 0;
        const why = c.why ?? r.signWhy ?? '';
        return (
          <div key={r.id} data-testid={`incident-${r.id}`} style={sx(CARD)}>
            <span style={sx('font-size:16px;font-weight:800;color:#22272E;line-height:1.3;')}>{[r.type, r.residentName, r.room].filter(Boolean).join(' · ')}</span>
            <span style={sx('font-size:16px;color:#54606C;')}>{`From ${r.reportedBy.name ?? 'staff'} · ${hhmm(r.reportedAt)} · ${r.status === 'signed' ? 'SIGNED' : 'DRAFT'}`}</span>
            {ck && (
              <span style={sx(`align-self:flex-start;font-size:14px;font-weight:800;border-radius:6px;padding:3px 8px;white-space:nowrap;color:#FFFFFF;background:${ck.red ? '#B3261E' : '#8A5A00'};`)}>{ck.text}</span>
            )}
            {r.investigationDueAt && <span style={sx('font-size:16px;color:#3C454F;')}>{`Investigation due ${day(r.investigationDueAt)}`}</span>}
            {r.whatHappened && <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>{r.whatHappened}</span>}
            {canAct ? (
              <>
                <div style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
                  <A
                    style={chip(!!r.doctorTold || telling[r.id] === 'doctor')}
                    onClick={() => !r.doctorTold && setTelling((p) => ({ ...p, [r.id]: p[r.id] === 'doctor' ? null : 'doctor' }))}
                    disabled={c.busy}
                  >
                    {r.doctorTold ? toldChip('Doctor told', r.doctorTold) : 'Tell the doctor'}
                  </A>
                  <A
                    style={chip(!!r.familyTold || telling[r.id] === 'family')}
                    onClick={() => !r.familyTold && setTelling((p) => ({ ...p, [r.id]: p[r.id] === 'family' ? null : 'family' }))}
                    disabled={c.busy}
                  >
                    {r.familyTold ? toldChip('Family told', r.familyTold) : 'Tell the family'}
                  </A>
                  {r.stateReport.required && (
                    <A style={chip(!!r.stateReport.filed)} onClick={() => !r.stateReport.filed && void act(r, 'filed')} disabled={c.busy}>
                      {r.stateReport.filed ? `State report filed · ${hhmm(r.stateReport.filed.at)}` : 'Mark state report filed'}
                    </A>
                  )}
                </div>
                {telling[r.id] === 'doctor' && !r.doctorTold && (
                  <TellDoctorPanel client={client} report={r} onDone={(res, t) => told(r, res, t)} onCancel={() => setTelling((p) => ({ ...p, [r.id]: null }))} />
                )}
                {telling[r.id] === 'family' && !r.familyTold && (
                  <TellFamilyPanel client={client} report={r} onDone={(res, t) => told(r, res, t)} onCancel={() => setTelling((p) => ({ ...p, [r.id]: null }))} />
                )}
                <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;')}>
                  <A style={prim(ready)} onClick={() => void act(r, 'sign')} disabled={c.busy}>
                    Sign
                  </A>
                  <span role={c.why ? 'alert' : undefined} style={sx('flex:1 1 160px;min-width:0;font-size:16px;color:#8A5A00;font-weight:700;')}>{why}</span>
                </div>
              </>
            ) : (
              <span style={sx('font-size:16px;color:#3C454F;')}>{stampLine(r)}</span>
            )}
            {r.familyTold?.how?.alerts?.callNow && (
              <span role="alert" data-testid={`family-call-now-${r.id}`} style={sx('font-size:16px;color:#8A1C16;font-weight:700;')}>{r.familyTold.how.alerts.callNow}</span>
            )}
            {c.error && <ErrorNote error={c.error} compact />}
            <span style={sx('font-size:14px;color:#54606C;')}>{escText(r)}</span>
          </div>
        );
      })}
      {types.data && (
        <span style={sx('font-size:16px;color:#54606C;line-height:1.45;')}>
          {types.data.rules.stateReportNote}
          <BuildLabel>{' SAMPLE until compliance confirms.'}</BuildLabel>
        </span>
      )}
    </PocDialog>
  );
}
